import type { ConflictResult, ConflictRule } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { isEmployeeActiveOnDate, type TimeTrackingPeriodSnapshot } from '../../utils/employment-capacity.utils';

/**
 * Detects conflicts when a person is scheduled on a date outside any of
 * their employment/team-membership periods.
 *
 * This is the reactive counterpart to the solver's proactive eligibility
 * filter (`EMPLOYMENT_INACTIVE` in slot-eligibility.utils.ts) — it catches
 * assignments that become invalid after the fact, e.g. an employment period
 * was shortened or removed after a plan was already solved.
 *
 * Persons with no entry in `personPeriods` are skipped (fall back to no
 * check), matching `ClosedDayConflictRule`'s fallback-to-team-pattern
 * behaviour for persons without an explicit mask.
 */
export class EmploymentInactiveConflictRule implements ConflictRule {
  readonly type = 'employment_inactive' as const;

  constructor(private readonly personPeriods: ReadonlyMap<string, TimeTrackingPeriodSnapshot[]> = new Map()) {}

  detect(schedule: ScheduleMap): ConflictResult[] {
    const results: ConflictResult[] = [];

    for (const [date, daySchedule] of Object.entries(schedule)) {
      for (const personId of Object.keys(daySchedule)) {
        const periods = this.personPeriods.get(personId);
        if (!periods || isEmployeeActiveOnDate(periods, date)) continue;

        results.push({
          type: 'employment_inactive',
          date,
          personId,
          eventLabel: 'No active employment',
          calendarId: 'strategy',
        });
      }
    }

    return results;
  }
}
